import React, { useState } from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { useIsMobile } from '../../../../util/style-utils';
import { OutageLookUpType } from '../../../../__generated__/pge-types';
import { CustomDialog } from '../common/custom-dialog';
import BoltIcon from '../../../../static/icon-bolt.svg';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { ReportOutageOtherLocationDetails } from '../report-outage-other-location-details/ReportOutageOtherLocationDetails';
import { makeStyles, Theme, Typography } from '@material-ui/core';
import { ToggleSwitch } from '../common/toggle-switch';
import { OutageTabs } from '../mobile-tabs-component/OutageTabs';
import useAuth from '../../../../hooks/useAuth';
import { OutageTypeExperienceDetails } from '../outage-type-experience-details/OutageTypeExperienceDetails';
import { ReportOutageSignIn } from '../report-outage-sign-in/ReportOutageSignIn';

const useStyles = (toggleSwitch?: boolean, isAuthenticated?: boolean) =>
  makeStyles((theme: Theme) => ({
    switchStyles: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'end',
      gap: '5px',
    },
    switchDiv: {
      marginTop: '-4rem',
      position: 'absolute',
      marginLeft: !toggleSwitch && !isAuthenticated ? '16rem' : '28rem',
    },
    root: {
      width: 44,
      height: 24,
      padding: 0,
      margin: theme.spacing(1),
    },
    thumb: {
      width: 20,
      height: 20,
    },
    font16: {
      fontSize: 16,
    },
    track1: {
      borderRadius: 32 / 2,
      backgroundColor: colors.sparkBlue,
      opacity: 1,
      transition: theme.transitions.create(['background-color', 'border']),
    },
    switchBase1: {
      padding: 2,
      left: 0,
      '&$checked': {
        transform: 'translateX(20px)',
        color: colors.white,
        '& + $track': {
          backgroundColor: theme.palette.grey[600],
          opacity: 1,
          border: 'none',
        },
      },
      '&$focusVisible $thumb': {
        color: theme.palette.grey[600],
      },
    },
  }));

export const ReportOutageOtherLocation = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    accountNotFoundInfo,
    setAccountNotFoundInfo,
  } = useOutageLeftPanelState();
  const [toggleSwitch, setToggleSwitch] = useState<boolean>(true);
  const [tabNumber, setTabNumber] = useState<number>(1);
  const [outageExperienceType, setOutageExperienceType] = useState<
    string | null
  >(null);
  const { isAuthenticated } = useAuth();
  const classes = useStyles(toggleSwitch, isAuthenticated)();

  const renderSwitchComp = () => {
    return (
      <div className={classes.switchStyles}>
        <Typography className={classes.font16}>{t('MY_ACCOUNT')}</Typography>
        <ToggleSwitch
          classes={{
            root: classes.root,
            thumb: classes.thumb,
            track: classes.track1,
            switchBase: classes.switchBase1,
          }}
          size={'medium'}
          checked={toggleSwitch}
          onChange={e => {
            setToggleSwitch(e.target.checked);
          }}
        />
        <Typography className={classes.font16}>
          {t('ANOTHER_LOCATION')}
        </Typography>
      </div>
    );
  };
  return (
    <CustomDialog
      open={accountNotFoundInfo?.accountNotFoundModalOpen || false}
      onClose={() => {
        setAccountNotFoundInfo?.({
          ...(accountNotFoundInfo ?? {}),
          accountNotFoundModalOpen: false,
          lookupType: OutageLookUpType.Account,
          lookupValue: undefined,
        });
      }}
      title={t('REPORT_OUTAGE')}
      dialogWidth={!toggleSwitch && !isAuthenticated ? '600px' : '800px'}
      titleBgColor={isMobile ? '' : colors.riverBlue}
      titleTextColor={isMobile ? '' : colors.noirBlur}
      titleIcon={isMobile ? <BoltIcon /> : ''}
    >
      {isMobile ? (
        <OutageTabs
          myAccountPanel={<ReportOutageSignIn />}
          anotherLocationPanel={<ReportOutageOtherLocationDetails />}
          tabNumber={tabNumber}
          setTabNumber={setTabNumber}
        />
      ) : (
        <>
          <div className={classes.switchDiv}>{renderSwitchComp()}</div>
          {toggleSwitch ? (
            <ReportOutageOtherLocationDetails />
          ) : isAuthenticated ? (
            <OutageTypeExperienceDetails
              outageExperienceType={outageExperienceType}
              setOutageExperienceType={setOutageExperienceType}
            />
          ) : (
            <ReportOutageSignIn />
          )}
        </>
      )}
    </CustomDialog>
  );
};
